import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Textarea } from '@/components/ui/Textarea';
import { DatePicker } from '@/components/ui/DatePicker';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useProfiles } from '@/features/users/hooks';
import { useProjects } from '@/features/projects/hooks';
import { useUpdateEngProject, type EngProject } from '@/features/engineering/hooks';
import { formatError } from '@/lib/errors';
import { centsToInputDollars, inputDollarsToCents } from '@/lib/formatters';

const NO_PM_PROJECT = '__none__';
const NO_OWNER = '__none__';

export function EditEngProjectDialog({
  project,
  open,
  onOpenChange,
  canManage,
}: {
  project: EngProject;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Owner can only be reassigned by the owner / creator / admins. */
  canManage: boolean;
}) {
  const update = useUpdateEngProject();
  const { data: profiles } = useProfiles();
  const { data: pmProjects } = useProjects({ includeArchived: true });

  const [code, setCode] = useState(project.code);
  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? '');
  const [ownerId, setOwnerId] = useState(project.owner_id ?? NO_OWNER);
  const [startDate, setStartDate] = useState(project.start_date ?? '');
  const [targetDate, setTargetDate] = useState(project.target_date ?? '');
  const [budget, setBudget] = useState(centsToInputDollars(project.budget_cents));
  const [pmProjectId, setPmProjectId] = useState(project.pm_project_id ?? NO_PM_PROJECT);

  useEffect(() => {
    if (open) {
      setCode(project.code);
      setName(project.name);
      setDescription(project.description ?? '');
      setOwnerId(project.owner_id ?? NO_OWNER);
      setStartDate(project.start_date ?? '');
      setTargetDate(project.target_date ?? '');
      setBudget(centsToInputDollars(project.budget_cents));
      setPmProjectId(project.pm_project_id ?? NO_PM_PROJECT);
    }
  }, [open, project]);

  const people = useMemo(
    () => (profiles ?? []).filter((p) => p.is_active !== false || p.id === project.owner_id),
    [profiles, project.owner_id],
  );

  const pmOptions = useMemo(() => {
    const list = (pmProjects ?? []).filter((p) => !p.archived_at || p.id === project.pm_project_id);
    return list;
  }, [pmProjects, project.pm_project_id]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    let budgetCents: number | null;
    try {
      budgetCents = inputDollarsToCents(budget);
    } catch (err) {
      toast.error(formatError(err));
      return;
    }
    if (startDate && targetDate && targetDate < startDate) {
      toast.error('The target date can’t be before the start date.');
      return;
    }
    try {
      await update.mutateAsync({
        id: project.id,
        patch: {
          code,
          name,
          description,
          owner_id: canManage ? (ownerId === NO_OWNER ? null : ownerId) : project.owner_id,
          start_date: startDate || null,
          target_date: targetDate || null,
          budget_cents: budgetCents,
          pm_project_id: pmProjectId === NO_PM_PROJECT ? null : pmProjectId,
        },
      });
      toast.success('Project updated.');
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err, 'Could not save the project.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit project details</DialogTitle>
          <DialogDescription>Status is changed from the header; team members from the Team card.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-[140px_1fr]">
            <div className="space-y-1.5">
              <Label htmlFor="edit-eng-code">Code</Label>
              <Input
                id="edit-eng-code"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="font-mono"
                maxLength={32}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-eng-name">Name</Label>
              <Input id="edit-eng-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} required />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-eng-description">Description</Label>
            <Textarea
              id="edit-eng-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="min-h-[96px]"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Owner</Label>
              <Select value={ownerId} onValueChange={setOwnerId} disabled={!canManage}>
                <SelectTrigger aria-label="Owner">
                  <SelectValue placeholder="No owner" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_OWNER}>No owner</SelectItem>
                  {people.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.full_name ?? p.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!canManage && (
                <p className="text-xs text-muted-foreground">Only the owner or an admin can reassign ownership.</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="edit-eng-budget">Budget</Label>
              <Input
                id="edit-eng-budget"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="$ 0.00"
                inputMode="decimal"
              />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Start date</Label>
              <DatePicker value={startDate} onChange={setStartDate} placeholder="Start" aria-label="Start date" />
            </div>
            <div className="space-y-1.5">
              <Label>Target date</Label>
              <DatePicker value={targetDate} onChange={setTargetDate} placeholder="Target" aria-label="Target date" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Task list (Tasks section)</Label>
            <Select value={pmProjectId} onValueChange={setPmProjectId}>
              <SelectTrigger aria-label="Task list">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_PM_PROJECT}>No task list</SelectItem>
                {pmOptions.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    {p.archived_at ? ' (archived)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={update.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
